import clsx from "clsx";

import { useSuiQueryContext } from "@/context/SUIQuery/useSuiQueryContext";
import currency from "@/lib/currency";
import { Button } from "@/ui/nextui/Button/Button";
import { Input } from "@/ui/nextui/Input/Input";
import { Checkbox } from "@/ui/shadcn/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/ui/shadcn/form";

import {
  PurchaseLicenseFormBodyProps,
  PurchaseLicenseFormProps,
} from "./PurchaseLicenseForm.types";

export const PurchaseLicenseForm: React.FC<PurchaseLicenseFormProps> & {
  Body: React.FC<PurchaseLicenseFormBodyProps>;
} = ({ children, className, id = "purchase-license-form", onSubmit }) => {
  const { purchaseLicenseForm } = useSuiQueryContext();

  return (
    <Form {...purchaseLicenseForm}>
      <form
        className={clsx(className)}
        id={id}
        onSubmit={purchaseLicenseForm.handleSubmit(onSubmit)}
      >
        {children}
      </form>
    </Form>
  );
};

const PurchaseLicenseFormBody: React.FC<PurchaseLicenseFormBodyProps> = () => {
  const { treasury, currentAccountCoins, purchaseLicenseForm, mergeCoinObjects } =
    useSuiQueryContext();

  function onClickMergeCoinObjects() {
    mergeCoinObjects({});
  }

  return (
    <>
      <div id="purchase-license-form-price-block">
        <h4 className="mb-0" id="purchase-license-form-price-value">
          {treasury.price.current.formatted}
        </h4>
        <p className="text-gray-500" id="purchase-license-form-price-label">
          License Price
        </p>
      </div>
      <div className="flex justify-between" id="purchase-license-form-tier-block">
        <div id="purchase-license-form-current-tier-stat">
          <h4 className="mb-0" id="purchase-license-form-current-tier-value">
            {treasury.currentTier}
          </h4>
          <p className="mb-0 text-gray-500" id="purchase-license-form-current-tier-label">
            Current Tier
          </p>
        </div>
        <div id="purchase-license-form-next-tier-stat">
          <h4 className="mb-0" id="purchase-license-form-next-tier-value">
            {treasury.salesToNextTier} Sales
          </h4>
          <p className="mb-0 text-gray-500" id="purchase-license-form-next-tier-label">
            # to Next Tier
          </p>
        </div>
      </div>

      <FormField
        control={purchaseLicenseForm.control}
        name="referralCode"
        render={({ field, fieldState }) => (
          <FormItem id="purchase-license-form-referral-code-item">
            <FormLabel id="purchase-license-form-referral-code-label">Referral Code</FormLabel>
            <FormControl>
              <Input
                disabled
                className="bg-green-700 [&_input]:text-center"
                color="success"
                isInvalid={!!fieldState.error}
                placeholder="Type in your referral code..."
                size="lg"
                variant="bordered"
                {...field}
              />
            </FormControl>

            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex gap-2" id="purchase-license-form-amount-row">
        <FormField
          control={purchaseLicenseForm.control}
          name="price"
          render={({ field }) => (
            <FormItem className="w-6/12" id="purchase-license-form-price-item">
              <FormLabel id="purchase-license-form-price-input-label">Price</FormLabel>
              <FormControl>
                <Input
                  disabled
                  placeholder={treasury.price.current.formatted}
                  size="lg"
                  type="text"
                  variant="bordered"
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={purchaseLicenseForm.control}
          name="quantity"
          render={({ field }) => (
            <FormItem className="w-6/12" id="purchase-license-form-quantity-item">
              <FormLabel id="purchase-license-form-quantity-input-label">Quantity</FormLabel>
              <FormControl>
                <Input
                  endContent={
                    <Button
                      isIconOnly
                      className="text-2xl"
                      id="purchase-license-form-quantity-increment-button"
                      type="button"
                      variant="light"
                      onClick={() => {
                        purchaseLicenseForm.setValue(field.name, String(Number(field.value) + 1), {
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                      }}
                    >
                      +
                    </Button>
                  }
                  placeholder="1"
                  size="lg"
                  startContent={
                    <Button
                      isIconOnly
                      className="text-2xl"
                      id="purchase-license-form-quantity-decrement-button"
                      type="button"
                      variant="light"
                      onClick={() => {
                        if (Number(field.value) === 1) return;

                        purchaseLicenseForm.setValue(field.name, String(Number(field.value) - 1), {
                          shouldTouch: true,
                          shouldValidate: true,
                        });
                      }}
                    >
                      -
                    </Button>
                  }
                  type="text"
                  variant="bordered"
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div id="purchase-license-form-balance-block">
        <p className="mb-0 text-xs text-gray-500" id="purchase-license-form-balance-text">
          Your account balance:{" "}
          <strong id="purchase-license-form-balance-value">
            {currentAccountCoins.totalCoinBalance?.formatted}
          </strong>{" "}
        </p>

        {currentAccountCoins.coins.length > 1 && (
          <strong
            className="mb-0 text-xs text-red-500"
            id="purchase-license-form-coin-merge-notice"
          >
            (is from {currentAccountCoins.coins.length} {currency.constants.DEFAULT_CURRENCY_TICKER}{" "}
            coin objects). You may want to{" "}
            <Button
              fullWidth
              className="my-1"
              color="warning"
              id="purchase-license-form-merge-coins-button"
              size="sm"
              type="button"
              variant="bordered"
              onClick={onClickMergeCoinObjects}
            >
              Merge Them
            </Button>
          </strong>
        )}

        {(currentAccountCoins.totalCoinBalance?.raw || 0n) < treasury.price.current.raw && (
          <p
            className="mb-0 text-xs text-red-500"
            id="purchase-license-form-insufficient-balance-warning"
          >
            Not enough balance to purchase 1 license. Transaction will fail.
          </p>
        )}
      </div>

      <FormField
        control={purchaseLicenseForm.control}
        name="isTermsCheckboxAccepted"
        render={({ field }) => (
          <>
            <FormItem className="flex items-center gap-2" id="purchase-license-form-terms-item">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel id="purchase-license-form-terms-label">
                I have read and agree to the{" "}
                <a
                  href="https://swarmnetwork.ai/tos"
                  id="purchase-license-form-terms-link"
                  target="_blank"
                >
                  terms of service
                </a>
              </FormLabel>
            </FormItem>
            <FormMessage />
          </>
        )}
      />
      <FormField
        control={purchaseLicenseForm.control}
        name="isPrivacyPolicyAccepted"
        render={({ field }) => (
          <>
            <FormItem className="flex items-center gap-2" id="purchase-license-form-privacy-item">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} />
              </FormControl>
              <FormLabel className="!mt-0" id="purchase-license-form-privacy-label">
                I have read and agree to the{" "}
                <a
                  href="https://swarmnetwork.ai/agentlicenseagreement"
                  id="purchase-license-form-privacy-link"
                  target="_blank"
                >
                  privacy policy
                </a>
              </FormLabel>
            </FormItem>
            <FormMessage />
          </>
        )}
      />
    </>
  );
};

PurchaseLicenseForm.Body = PurchaseLicenseFormBody;
